import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError, apiFetch, setCsrfToken } from './client';
import type {
  AgBulkResult,
  AgImportResult,
  AgRequestOverview,
  NobleCandidatesResult,
  NobleExportResult,
  AgPlanDetail,
  AgPlanSummary,
  AgPlanerMap,
  AgPlanerTerrain,
  AttackPackageCategory,
  AttackPackageTemplate,
  AttackPlan,
  AttackPlanMode,
  AttackPlanStatus,
  AttackPlanTarget,
  BatchInsertOutcome,
  DiscoveredWorld,
  DsWorkbenchExportResult,
  GroupPastedWorkbenchResult,
  FeasibilityResult,
  GameEntitySearchResult,
  IncomingAttack,
  ManagedEntity,
  ManagedEntityKind,
  PoolVillage,
  Report,
  DiscordNotificationSetting,
  DiscordNotificationType,
  RetimeRequest,
  SdRequest,
  SendWindow,
  SessionResponse,
  User,
  UserFilter,
  UserListResponse,
  UserRole,
  UserscriptLatest,
  UserscriptVersion,
  UserStatus,
  VillageIntel,
  World,
  WorldOption,
  WorldStatus,
} from './types';

export const queryKeys = {
  session: ['session'] as const,
  users: (filter: UserFilter) => ['admin', 'users', filter] as const,
  worlds: (purpose: 'login' | 'register') => ['worlds', purpose] as const,
  adminWorlds: ['admin', 'worlds'] as const,
  reports: (filter: ReportListFilter) => ['reports', filter] as const,
  incomingAttacks: (filter: IncomingAttackListFilter) => ['incomings', filter] as const,
  villageIntel: (filter: VillageIntelListFilter) => ['village-intel', filter] as const,
  sendWindows: ['account', 'send-windows'] as const,
  managedEntities: (welt: string | undefined) => ['verwaltung', 'entities', welt] as const,
  sdRequests: (welt: string | undefined) => ['sd-anfragen', welt] as const,
  retimeRequests: (welt: string | undefined) => ['retime-anfragen', welt] as const,
  adminUserscriptVersions: ['admin', 'userskript'] as const,
  userscriptLatest: ['userskript', 'aktuell'] as const,
  discordSettings: (welt: string | undefined) => ['discord-einstellungen', welt] as const,
  attackPackageTemplates: (welt: string | undefined) => ['angriffsplaner', 'pakettypen', welt] as const,
  agPlanerMap: (welt: string | undefined) => ['ag-planer', 'karte', welt] as const,
  agPlanerTerrain: (welt: string | undefined) => ['ag-planer', 'gelaende', welt] as const,
  agPlans: (welt: string | undefined) => ['ag-planer', 'plaene', welt] as const,
  agPlan: (welt: string | undefined, id: number | null) => ['ag-planer', 'plan', welt, id] as const,
  attackPlans: (welt: string | undefined) => ['angriffsplaner', 'plaene', welt] as const,
  attackPlan: (welt: string | undefined, id: number) => ['angriffsplaner', 'plan', welt, id] as const,
  attackPlanCheck: (welt: string | undefined, id: number) => ['angriffsplaner', 'plan', welt, id, 'pruefung'] as const,
};

/* ------------------------------------------------------------------ */
/* Sitzung                                                             */
/* ------------------------------------------------------------------ */

export function useSession(): UseQueryResult<SessionResponse> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: async () => {
      const data = await apiFetch<SessionResponse>('/session');
      setCsrfToken(data.csrfToken);
      return data;
    },
    // Die Sitzung ist die Grundlage aller Seiten: nie stumm veralten lassen.
    staleTime: 30_000,
    retry: false,
  });
}

export function useLogin(): UseMutationResult<
  SessionResponse,
  Error,
  { worldKey?: string; username: string; password: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) => apiFetch<SessionResponse>('/session', { method: 'POST', body: input }),
    onSuccess: (data) => {
      // Nach der Anmeldung gilt eine neue Session-ID und ein neues Token.
      setCsrfToken(data.csrfToken);
      queryClient.setQueryData(queryKeys.session, data);
    },
  });
}

export function useLogout(): UseMutationResult<SessionResponse, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiFetch<void>('/session', { method: 'DELETE' });

      // Nach dem Abmelden gilt eine neue, anonyme Session mit eigenem Token.
      const next = await apiFetch<SessionResponse>('/session');
      setCsrfToken(next.csrfToken);
      return next;
    },
    onSuccess: (next) => {
      // Bewusst kein queryClient.clear(): das würde auch den Mutations-Cache
      // leeren und diese Mutation selbst abbrechen, bevor der Aufrufer sein
      // onSuccess bekommt. Stattdessen gezielt die Daten der alten Sitzung
      // verwerfen und die Sitzung sofort neu besetzen.
      queryClient.removeQueries({ queryKey: ['admin'] });
      queryClient.removeQueries({ queryKey: ['worlds'] });
      queryClient.setQueryData(queryKeys.session, next);
    },
  });
}

export function useRegister(): UseMutationResult<
  { user: User },
  Error,
  { worldKey: string; username: string; password: string; passwordConfirm: string }
> {
  return useMutation({
    mutationFn: (input) => apiFetch<{ user: User }>('/register', { method: 'POST', body: input }),
  });
}

export function useChangePassword(): UseMutationResult<
  void,
  Error,
  { currentPassword: string; newPassword: string; newPasswordConfirm: string }
> {
  return useMutation({
    mutationFn: (input) => apiFetch<void>('/account/password', { method: 'PUT', body: input }),
  });
}

/* ------------------------------------------------------------------ */
/* Usertoken                                                           */
/* ------------------------------------------------------------------ */

/**
 * Schreibt den geänderten Benutzer zurück in die Sitzung, damit Kopfzeile
 * und Kontoseite sofort den neuen Stand zeigen.
 */
function useUpdateSessionUser(): (user: User) => void {
  const queryClient = useQueryClient();

  return (user: User) => {
    queryClient.setQueryData(queryKeys.session, (previous: SessionResponse | undefined) =>
      previous ? { ...previous, user } : previous,
    );
  };
}

/**
 * Erzeugt einen Usertoken. Der Klartext steht **nur** in dieser einen Antwort
 * und wird bewusst nirgends zwischengespeichert.
 */
export function useIssueToken(): UseMutationResult<{ token: string; user: User }, Error, void> {
  const updateSessionUser = useUpdateSessionUser();

  return useMutation({
    mutationFn: () => apiFetch<{ token: string; user: User }>('/account/token', { method: 'POST' }),
    onSuccess: (data) => updateSessionUser(data.user),
  });
}

export function useRevokeToken(): UseMutationResult<{ user: User }, Error, void> {
  const updateSessionUser = useUpdateSessionUser();

  return useMutation({
    mutationFn: () => apiFetch<{ user: User }>('/account/token', { method: 'DELETE' }),
    onSuccess: (data) => updateSessionUser(data.user),
  });
}

/** Notfallweg für Administratoren: fremden Token entwerten. */
export function useRevokeUserToken(): UseMutationResult<{ user: User }, Error, { id: number }> {
  const invalidate = useInvalidateUsers();

  return useMutation({
    mutationFn: ({ id }) => apiFetch<{ user: User }>(`/admin/users/${id}/token`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* Spielfigur                                                          */
/* ------------------------------------------------------------------ */

/**
 * Hinterlegt (oder löscht, bei `name: null`) den Spielernamen. Damit lässt
 * sich beim Einlesen ein Bericht automatisch als Angriff oder Verteidigung
 * erkennen, ohne bei jedem Bericht danach zu fragen.
 */
/**
 * Verifizierungs-Wizard: Schritt 1 erzeugt zu einem Spielernamen einen Code
 * (`user.verification` in der Antwort), Schritt 2 prüft, ob er im
 * öffentlichen Profil dieses Spielers steht.
 */
export function useStartVerification(): UseMutationResult<{ user: User }, Error, { playerName: string }> {
  const updateSessionUser = useUpdateSessionUser();

  return useMutation({
    mutationFn: (input) => apiFetch<{ user: User }>('/account/verification', { method: 'POST', body: input }),
    onSuccess: (data) => updateSessionUser(data.user),
  });
}

export function useConfirmVerification(): UseMutationResult<{ user: User }, Error, void> {
  const updateSessionUser = useUpdateSessionUser();

  return useMutation({
    mutationFn: () => apiFetch<{ user: User }>('/account/verification/confirm', { method: 'POST' }),
    onSuccess: (data) => updateSessionUser(data.user),
  });
}

export function useCancelVerification(): UseMutationResult<{ user: User }, Error, void> {
  const updateSessionUser = useUpdateSessionUser();

  return useMutation({
    mutationFn: () => apiFetch<{ user: User }>('/account/verification', { method: 'DELETE' }),
    onSuccess: (data) => updateSessionUser(data.user),
  });
}

/* ------------------------------------------------------------------ */
/* Benutzerverwaltung                                                  */
/* ------------------------------------------------------------------ */

function toQueryString(filter: UserFilter): string {
  const params = new URLSearchParams();
  if (filter.q) params.set('q', filter.q);
  if (filter.status) params.set('status', filter.status);
  if (filter.role) params.set('role', filter.role);
  if (filter.welt) params.set('welt', filter.welt);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/* ------------------------------------------------------------------ */
/* Welten                                                              */
/* ------------------------------------------------------------------ */

/**
 * Öffentliche Weltliste für die Auswahlfelder. `register` liefert nur offene
 * Welten, `login` zusätzlich die geschlossenen.
 */
export function useWorldOptions(
  purpose: 'login' | 'register',
): UseQueryResult<{ worlds: WorldOption[] }> {
  return useQuery({
    queryKey: queryKeys.worlds(purpose),
    queryFn: () => apiFetch<{ worlds: WorldOption[] }>(`/worlds?zweck=${purpose}`),
    staleTime: 60_000,
  });
}

export function useAdminWorlds(): UseQueryResult<{ worlds: World[] }> {
  return useQuery({
    queryKey: queryKeys.adminWorlds,
    queryFn: ({ signal }) => apiFetch<{ worlds: World[] }>('/admin/worlds', { signal }),
    staleTime: 10_000,
  });
}

/**
 * Änderungen an Welten wirken sich auf die Auswahlfelder und auf die
 * Benutzerliste aus, deshalb wird beides mit verworfen.
 */
function useInvalidateWorlds(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'worlds'] }),
      queryClient.invalidateQueries({ queryKey: ['worlds'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
    ]);
  };
}

export function useCreateWorld(): UseMutationResult<
  { world: World },
  Error,
  { key: string; name: string; status?: WorldStatus; syncHost?: string }
> {
  const invalidate = useInvalidateWorlds();

  return useMutation({
    mutationFn: (input) => apiFetch<{ world: World }>('/admin/worlds', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateWorld(): UseMutationResult<
  { world: World },
  Error,
  {
    id: number;
    name?: string;
    status?: WorldStatus;
    syncHost?: string | null;
    villageIntelSubstantialBp?: number | null;
    villageIntelDefaultDays?: number | null;
    villageIntelMaxAxe?: number | null;
    villageIntelMaxLight?: number | null;
    villageIntelMaxRam?: number | null;
  }
> {
  const invalidate = useInvalidateWorlds();

  return useMutation({
    mutationFn: ({ id, ...changes }) =>
      apiFetch<{ world: World }>(`/admin/worlds/${id}`, { method: 'PATCH', body: changes }),
    onSuccess: invalidate,
  });
}

export function useDeleteWorld(): UseMutationResult<{ world: World }, Error, { id: number }> {
  const invalidate = useInvalidateWorlds();

  return useMutation({
    mutationFn: ({ id }) => apiFetch<{ world: World }>(`/admin/worlds/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/**
 * Fragt eine Spielplattform (z. B. die-staemme.de) nach ihrer aktuellen
 * Weltliste -- Hilfe beim Anlegen, kein Dauerzustand, daher eine Mutation
 * statt einer Query.
 */
export function useDiscoverWorlds(): UseMutationResult<
  { worlds: DiscoveredWorld[] },
  Error,
  { host: string }
> {
  return useMutation({
    mutationFn: ({ host }) =>
      apiFetch<{ worlds: DiscoveredWorld[] }>(`/admin/worlds/discover?host=${encodeURIComponent(host)}`),
  });
}

/** Stößt Dörfer/Spieler/Stämme sofort neu an, statt auf den stündlichen Takt zu warten. */
export function useSyncWorld(): UseMutationResult<{ world: World }, Error, { id: number }> {
  const invalidate = useInvalidateWorlds();

  return useMutation({
    mutationFn: ({ id }) => apiFetch<{ world: World }>(`/admin/worlds/${id}/sync`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useUsers(
  filter: UserFilter,
  options: { enabled?: boolean } = {},
): UseQueryResult<UserListResponse> {
  return useQuery({
    queryKey: queryKeys.users(filter),
    queryFn: ({ signal }) =>
      apiFetch<UserListResponse>(`/admin/users${toQueryString(filter)}`, { signal }),
    // Beim Wechsel des Filters die alte Liste stehen lassen, statt zu flackern.
    placeholderData: (previous) => previous,
    staleTime: 10_000,
    enabled: options.enabled ?? true,
  });
}

/** Invalidiert jede Variante der Benutzerliste, egal welcher Filter aktiv ist. */
function useInvalidateUsers(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };
}

export function useUpdateUser(): UseMutationResult<
  { user: User },
  Error,
  { id: number; status?: UserStatus; role?: UserRole; worldKey?: string | null }
> {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateUsers();

  return useMutation({
    mutationFn: ({ id, ...changes }) =>
      apiFetch<{ user: User }>(`/admin/users/${id}`, { method: 'PATCH', body: changes }),
    onSuccess: async () => {
      await invalidate();
      // Kontozahlen je Welt ändern sich mit.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'worlds'] });
    },
  });
}

export function useResetUserPassword(): UseMutationResult<
  { user: User },
  Error,
  { id: number; newPassword: string }
> {
  const invalidate = useInvalidateUsers();

  return useMutation({
    mutationFn: ({ id, newPassword }) =>
      apiFetch<{ user: User }>(`/admin/users/${id}/password`, {
        method: 'PUT',
        body: { newPassword },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteUser(): UseMutationResult<{ user: User }, Error, { id: number }> {
  const invalidate = useInvalidateUsers();

  return useMutation({
    mutationFn: ({ id }) => apiFetch<{ user: User }>(`/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* Kampfberichte                                                       */
/* ------------------------------------------------------------------ */

export interface ReportListFilter {
  welt?: string;
  /** Nur Berichte, in denen die eigene Spielfigur Angreifer oder Verteidiger war. */
  eigene?: boolean;
  spieler?: string;
  dorf?: string;
  /** Als "x|y" -- Herkunft oder Ziel. */
  koordinaten?: string;
  /** 1-basiert. */
  seite?: number;
}

export interface ReportListResponse {
  reports: Report[];
  world: { key: string; name: string };
  /** Ob überhaupt eine Spielfigur hinterlegt ist -- erklärt eine leere "eigene"-Liste. */
  hasGamePlayer: boolean;
  total: number;
  page: number;
  pageSize: number;
}

export function useReports(filter: ReportListFilter = {}): UseQueryResult<ReportListResponse> {
  const params = new URLSearchParams();
  if (filter.welt) params.set('welt', filter.welt);
  if (filter.eigene !== undefined) params.set('eigene', filter.eigene ? '1' : '0');
  if (filter.spieler) params.set('spieler', filter.spieler);
  if (filter.dorf) params.set('dorf', filter.dorf);
  if (filter.koordinaten) params.set('koordinaten', filter.koordinaten);
  if (filter.seite) params.set('seite', String(filter.seite));
  const query = params.toString();

  return useQuery({
    queryKey: queryKeys.reports(filter),
    queryFn: ({ signal }) =>
      apiFetch<ReportListResponse>(`/reports${query ? `?${query}` : ''}`, { signal }),
    // Ohne Welt kann die Liste nicht ermittelt werden.
    enabled: filter.welt !== '',
    placeholderData: (previous) => previous,
    staleTime: 10_000,
  });
}

function useInvalidateReports(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['reports'] });
  };
}

/**
 * Lässt den Server einen oder mehrere Berichte holen und auswerten -- ein
 * einzelner Link, mehrere Links untereinander oder ein weitergeleiteter
 * Sammelbericht (`[report]…[/report]`-Tags) werden gleich behandelt.
 * Angriff/Verteidigung wird automatisch erkannt (anhand der am Konto
 * hinterlegten Spielfigur); ein Bericht, der keine Kampftabellen hat, taucht
 * im Ergebnis als fehlgeschlagenes Element auf, statt die übrigen zu blockieren.
 */
export function useInsertReport(): UseMutationResult<
  BatchInsertOutcome,
  Error,
  { input: string; worldKey?: string }
> {
  const invalidate = useInvalidateReports();

  return useMutation({
    mutationFn: (input) => apiFetch<BatchInsertOutcome>('/reports', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* Aktuelle Angriffe                                                    */
/* ------------------------------------------------------------------ */

export interface IncomingAttackListFilter {
  welt?: string;
  /** Nur Angriffe, in denen die eigene Spielfigur Angreifer oder Verteidiger ist. */
  eigene?: boolean;
  ziel?: string;
  herkunft?: string;
  angreifer?: string;
  verteidiger?: string;
}

export interface IncomingAttackListResponse {
  attacks: IncomingAttack[];
  world: { key: string; name: string };
  /** Ob überhaupt eine Spielfigur hinterlegt ist -- erklärt eine leere "eigene"-Liste. */
  hasGamePlayer: boolean;
}

/**
 * Läuft alle 20s neu ab -- die Liste lebt vom automatischen Upload durch das
 * Userscript, ohne eigenes Zutun der Betrachtenden. Ein manueller
 * Aktualisieren-Knopf bräuchte es sonst zusätzlich.
 */
export function useIncomingAttacks(filter: IncomingAttackListFilter): UseQueryResult<IncomingAttackListResponse> {
  const params = new URLSearchParams();
  if (filter.welt) params.set('welt', filter.welt);
  if (filter.eigene !== undefined) params.set('eigene', filter.eigene ? '1' : '0');
  if (filter.ziel) params.set('ziel', filter.ziel);
  if (filter.herkunft) params.set('herkunft', filter.herkunft);
  if (filter.angreifer) params.set('angreifer', filter.angreifer);
  if (filter.verteidiger) params.set('verteidiger', filter.verteidiger);
  const query = params.toString();

  return useQuery({
    queryKey: queryKeys.incomingAttacks(filter),
    queryFn: ({ signal }) =>
      apiFetch<IncomingAttackListResponse>(`/incomings${query ? `?${query}` : ''}`, { signal }),
    enabled: filter.welt !== '',
    refetchInterval: 20_000,
    placeholderData: (previous) => previous,
  });
}

/* ------------------------------------------------------------------ */
/* Off-/Deff-Dörfer                                                     */
/* ------------------------------------------------------------------ */

export interface VillageIntelListFilter {
  welt?: string;
  /** Zeitraum in Tagen, wie weit zurück Berichte betrachtet werden. */
  tage?: number;
}

export interface VillageIntelListResponse {
  villages: VillageIntel[];
  world: { key: string; name: string };
  /** `null` -- kein Zeitraum: alle gespeicherten Dörfer (die Einordnung bleibt bestehen, bis ein neuer Bericht etwas anderes belegt). */
  days: number | null;
}

export function useVillageIntel(filter: VillageIntelListFilter): UseQueryResult<VillageIntelListResponse> {
  const params = new URLSearchParams();
  if (filter.welt) params.set('welt', filter.welt);
  if (filter.tage) params.set('tage', String(filter.tage));
  const query = params.toString();

  return useQuery({
    queryKey: queryKeys.villageIntel(filter),
    queryFn: ({ signal }) =>
      apiFetch<VillageIntelListResponse>(`/village-intel${query ? `?${query}` : ''}`, { signal }),
    enabled: filter.welt !== '',
    placeholderData: (previous) => previous,
  });
}

/* ------------------------------------------------------------------ */
/* Abschickzeiten                                                      */
/* ------------------------------------------------------------------ */

export function useSendWindows(): UseQueryResult<{ windows: SendWindow[] }> {
  return useQuery({
    queryKey: queryKeys.sendWindows,
    queryFn: ({ signal }) => apiFetch<{ windows: SendWindow[] }>('/account/send-windows', { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateSendWindows(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.sendWindows });
  };
}

export function useCreateSendWindow(): UseMutationResult<
  { window: SendWindow },
  Error,
  { startAt: string; endAt: string }
> {
  const invalidate = useInvalidateSendWindows();

  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ window: SendWindow }>('/account/send-windows', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateSendWindow(): UseMutationResult<
  { window: SendWindow },
  Error,
  { id: number; startAt: string; endAt: string }
> {
  const invalidate = useInvalidateSendWindows();

  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiFetch<{ window: SendWindow }>(`/account/send-windows/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

export function useDeleteSendWindow(): UseMutationResult<{ window: SendWindow }, Error, { id: number }> {
  const invalidate = useInvalidateSendWindows();

  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<{ window: SendWindow }>(`/account/send-windows/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* Verwaltung -- verwaltete Spieler und Stämme                         */
/* ------------------------------------------------------------------ */

export function useManagedEntities(welt: string | undefined): UseQueryResult<{ entities: ManagedEntity[] }> {
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';
  return useQuery({
    queryKey: queryKeys.managedEntities(welt),
    queryFn: ({ signal }) => apiFetch<{ entities: ManagedEntity[] }>(`/verwaltung${query}`, { signal }),
    staleTime: 10_000,
  });
}

/**
 * Sucht in den zuletzt synchronisierten Weltdaten -- Grundlage der
 * Autovervollständigung beim Hinzufügen. `query` selbst wird bewusst nicht
 * hier entprellt (siehe `useDebounced` beim Aufrufer), sonst würde jede
 * Komponente, die diesen Hook nutzt, ihre eigene Verzögerung mitbringen.
 */
export function useSearchGameEntities(
  welt: string | undefined,
  kind: ManagedEntityKind,
  query: string,
): UseQueryResult<{ results: GameEntitySearchResult[] }> {
  const params = new URLSearchParams({ art: kind, suche: query });
  if (welt) params.set('welt', welt);

  return useQuery({
    queryKey: ['verwaltung', 'suche', welt, kind, query] as const,
    queryFn: ({ signal }) =>
      apiFetch<{ results: GameEntitySearchResult[] }>(`/verwaltung/suche?${params.toString()}`, { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateManagedEntities(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.managedEntities(welt) });
  };
}

export function useAddManagedEntity(
  welt: string | undefined,
): UseMutationResult<{ entity: ManagedEntity }, Error, { kind: ManagedEntityKind; gameId: number }> {
  const invalidate = useInvalidateManagedEntities(welt);

  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ entity: ManagedEntity }>('/verwaltung', {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveManagedEntity(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateManagedEntities(welt);
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';

  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/verwaltung/${id}${query}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* SD-Anfragen -- Sammeldeff-Paket-Anfragen                             */
/* ------------------------------------------------------------------ */

export function useSdRequests(welt: string | undefined): UseQueryResult<{ requests: SdRequest[] }> {
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';
  return useQuery({
    queryKey: queryKeys.sdRequests(welt),
    queryFn: ({ signal }) => apiFetch<{ requests: SdRequest[] }>(`/sd-anfragen${query}`, { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateSdRequests(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.sdRequests(welt) });
  };
}

export function useCreateSdRequest(
  welt: string | undefined,
): UseMutationResult<
  { request: SdRequest },
  Error,
  { villageX: number; villageY: number; packagesRequested: number; description: string }
> {
  const invalidate = useInvalidateSdRequests(welt);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ request: SdRequest }>('/sd-anfragen', {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useApproveSdRequest(
  welt: string | undefined,
): UseMutationResult<{ request: SdRequest }, Error, { id: number }> {
  const invalidate = useInvalidateSdRequests(welt);
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<{ request: SdRequest }>(`/sd-anfragen/${id}/genehmigen`, {
        method: 'POST',
        body: welt ? { worldKey: welt } : {},
      }),
    onSuccess: invalidate,
  });
}

/** `fulfilled: true` heißt: die Anfrage wurde durch diesen Beitrag erfüllt und wurde bereits gelöscht -- `request` fehlt dann. */
export function useContributeToSdRequest(
  welt: string | undefined,
): UseMutationResult<{ fulfilled: boolean; request?: SdRequest }, Error, { id: number; amount: number }> {
  const invalidate = useInvalidateSdRequests(welt);
  return useMutation({
    mutationFn: ({ id, amount }) =>
      apiFetch<{ fulfilled: boolean; request?: SdRequest }>(`/sd-anfragen/${id}/beitragen`, {
        method: 'POST',
        body: { amount, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteSdRequest(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateSdRequests(welt);
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';

  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/sd-anfragen/${id}${query}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* Retime-Anfragen                                                      */
/* ------------------------------------------------------------------ */

export function useRetimeRequests(welt: string | undefined): UseQueryResult<{ requests: RetimeRequest[] }> {
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';
  return useQuery({
    queryKey: queryKeys.retimeRequests(welt),
    queryFn: ({ signal }) => apiFetch<{ requests: RetimeRequest[] }>(`/retime-anfragen${query}`, { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateRetimeRequests(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.retimeRequests(welt) });
  };
}

export function useCreateRetimeRequest(
  welt: string | undefined,
): UseMutationResult<{ request: RetimeRequest }, Error, { villageX: number; villageY: number; arrivalTime: string }> {
  const invalidate = useInvalidateRetimeRequests(welt);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ request: RetimeRequest }>('/retime-anfragen', {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useConfirmRetimeRequest(
  welt: string | undefined,
): UseMutationResult<{ request: RetimeRequest }, Error, { id: number }> {
  const invalidate = useInvalidateRetimeRequests(welt);
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<{ request: RetimeRequest }>(`/retime-anfragen/${id}/bestaetigen`, {
        method: 'POST',
        body: welt ? { worldKey: welt } : {},
      }),
    onSuccess: invalidate,
  });
}

export function useUnconfirmRetimeRequest(
  welt: string | undefined,
): UseMutationResult<{ request: RetimeRequest }, Error, { id: number }> {
  const invalidate = useInvalidateRetimeRequests(welt);
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<{ request: RetimeRequest }>(`/retime-anfragen/${id}/bestaetigen${query}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useDeleteRetimeRequest(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateRetimeRequests(welt);
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';

  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/retime-anfragen/${id}${query}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ */
/* Userskript-Verwaltung                                                */
/* ------------------------------------------------------------------ */

export function useAdminUserscriptVersions(): UseQueryResult<{ versions: UserscriptVersion[] }> {
  return useQuery({
    queryKey: queryKeys.adminUserscriptVersions,
    queryFn: ({ signal }) => apiFetch<{ versions: UserscriptVersion[] }>('/admin/userskript', { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateUserscriptVersions(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUserscriptVersions }),
      queryClient.invalidateQueries({ queryKey: queryKeys.userscriptLatest }),
    ]);
  };
}

export function useUploadUserscriptVersion(): UseMutationResult<
  { version: UserscriptVersion },
  Error,
  { content: string; description?: string }
> {
  const invalidate = useInvalidateUserscriptVersions();
  return useMutation({
    mutationFn: (input) => apiFetch<{ version: UserscriptVersion }>('/admin/userskript', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function usePublishUserscriptVersion(): UseMutationResult<{ version: UserscriptVersion }, Error, { id: number }> {
  const invalidate = useInvalidateUserscriptVersions();
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<{ version: UserscriptVersion }>(`/admin/userskript/${id}/veroeffentlichen`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useDeleteUserscriptVersion(): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateUserscriptVersions();
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/admin/userskript/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Für die kleine Dashboard-Box -- `latest: null`, solange nichts veröffentlicht wurde. */
export function useUserscriptLatest(): UseQueryResult<{ latest: UserscriptLatest | null }> {
  return useQuery({
    queryKey: queryKeys.userscriptLatest,
    queryFn: ({ signal }) => apiFetch<{ latest: UserscriptLatest | null }>('/userscript/aktuell', { signal }),
    staleTime: 60_000,
  });
}

/* ------------------------------------------------------------------ */
/* Discord-Benachrichtigungen                                          */
/* ------------------------------------------------------------------ */

export function useDiscordNotificationSettings(
  welt: string | undefined,
): UseQueryResult<{ settings: DiscordNotificationSetting[] }> {
  const query = welt ? `?welt=${encodeURIComponent(welt)}` : '';
  return useQuery({
    queryKey: queryKeys.discordSettings(welt),
    queryFn: ({ signal }) =>
      apiFetch<{ settings: DiscordNotificationSetting[] }>(`/discord-einstellungen${query}`, { signal }),
    staleTime: 10_000,
  });
}

export function useUpdateDiscordNotificationSetting(
  welt: string | undefined,
): UseMutationResult<
  { setting: DiscordNotificationSetting },
  Error,
  { type: DiscordNotificationType; enabled: boolean; webhookUrl: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, ...input }) =>
      apiFetch<{ setting: DiscordNotificationSetting }>(`/discord-einstellungen/${type}`, {
        method: 'PUT',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.discordSettings(welt) });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Angriffsplaner                                                       */
/* ------------------------------------------------------------------ */

function weltQuery(welt: string | undefined): string {
  return welt ? `?welt=${encodeURIComponent(welt)}` : '';
}

export interface AttackPackageTemplateInput {
  name: string;
  category: AttackPackageCategory;
  minUnitsText: string;
  exportUnitsText: string;
  catapultTarget: string | null;
  dsWorkbenchType: number | null;
  priority: number;
  preventSplitPerVillage: boolean;
}

export function useAttackPackageTemplates(welt: string | undefined): UseQueryResult<{ templates: AttackPackageTemplate[] }> {
  return useQuery({
    queryKey: queryKeys.attackPackageTemplates(welt),
    queryFn: ({ signal }) => apiFetch<{ templates: AttackPackageTemplate[] }>(`/angriffsplaner/package-templates${weltQuery(welt)}`, { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateAttackPackageTemplates(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.attackPackageTemplates(welt) });
  };
}

export function useCreateAttackPackageTemplate(
  welt: string | undefined,
): UseMutationResult<{ template: AttackPackageTemplate }, Error, AttackPackageTemplateInput> {
  const invalidate = useInvalidateAttackPackageTemplates(welt);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ template: AttackPackageTemplate }>('/angriffsplaner/package-templates', {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateAttackPackageTemplate(
  welt: string | undefined,
): UseMutationResult<{ template: AttackPackageTemplate }, Error, { id: number } & AttackPackageTemplateInput> {
  const invalidate = useInvalidateAttackPackageTemplates(welt);
  return useMutation({
    mutationFn: ({ id, ...input }) =>
      apiFetch<{ template: AttackPackageTemplate }>(`/angriffsplaner/package-templates/${id}`, {
        method: 'PUT',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteAttackPackageTemplate(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateAttackPackageTemplates(welt);
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/angriffsplaner/package-templates/${id}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useAttackPlans(welt: string | undefined): UseQueryResult<{ plans: AttackPlan[] }> {
  return useQuery({
    queryKey: queryKeys.attackPlans(welt),
    queryFn: ({ signal }) => apiFetch<{ plans: AttackPlan[] }>(`/angriffsplaner/plans${weltQuery(welt)}`, { signal }),
    staleTime: 10_000,
  });
}

function useInvalidateAttackPlans(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.attackPlans(welt) });
  };
}

export function useCreateAttackPlan(
  welt: string | undefined,
): UseMutationResult<
  { plan: AttackPlan },
  Error,
  { name: string; mode: AttackPlanMode; wavesPerTarget: number | null; fakePackageTemplateId: number | null; fakeArrivalStart: number | null; fakeArrivalEnd: number | null }
> {
  const invalidate = useInvalidateAttackPlans(welt);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ plan: AttackPlan }>('/angriffsplaner/plans', {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export interface UpdateAttackPlanInput {
  id: number;
  name?: string;
  status?: AttackPlanStatus;
  /** Nur im Katta-Modus wirksam -- siehe attack-planner.service.ts's Validierung. */
  wavesPerTarget?: number | null;
  fakePackageTemplateId?: number | null;
  fakeArrivalStart?: number | null;
  fakeArrivalEnd?: number | null;
}

export function useUpdateAttackPlan(welt: string | undefined): UseMutationResult<{ plan: AttackPlan }, Error, UpdateAttackPlanInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }) =>
      apiFetch<{ plan: AttackPlan }>(`/angriffsplaner/plans/${id}`, {
        method: 'PATCH',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.attackPlans(welt) }),
        // Die Detailseite zeigt Status/Katta-Einstellungen/Ankunftszeit-
        // Vorbelegung aus derselben Query -- ohne diese Invalidierung bliebe
        // sie nach einer Änderung auf dem alten Stand (siehe der analoge Fix
        // bei useRunAttackPlanCheck).
        queryClient.invalidateQueries({ queryKey: queryKeys.attackPlan(welt, variables.id) }),
      ]);
    },
  });
}

export function useDeleteAttackPlan(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateAttackPlans(welt);
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/angriffsplaner/plans/${id}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export interface AttackPlanDetail {
  plan: AttackPlan;
  targets: AttackPlanTarget[];
  pool: PoolVillage[];
}

export function useAttackPlanDetail(welt: string | undefined, id: number): UseQueryResult<AttackPlanDetail> {
  return useQuery({
    queryKey: queryKeys.attackPlan(welt, id),
    queryFn: ({ signal }) => apiFetch<AttackPlanDetail>(`/angriffsplaner/plans/${id}${weltQuery(welt)}`, { signal }),
    staleTime: 5_000,
  });
}

function useInvalidateAttackPlanDetail(welt: string | undefined, planId: number): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.attackPlan(welt, planId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.attackPlans(welt) }),
    ]);
  };
}

export function useAddAttackPlanTargets(
  welt: string | undefined,
  planId: number,
): UseMutationResult<
  { created: number; reused: number; invalid: string[]; touchedTargetIds: number[] },
  Error,
  { coordsText: string; wave?: { packageTemplateId: number; count: number; arrivalStart: number; arrivalEnd: number | null; critical: boolean } }
> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: (input) =>
      apiFetch(`/angriffsplaner/plans/${planId}/targets`, {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteAttackPlanTarget(welt: string | undefined, planId: number): UseMutationResult<void, Error, { targetId: number }> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: ({ targetId }) =>
      apiFetch<void>(`/angriffsplaner/plans/${planId}/targets/${targetId}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useAddAttackPlanWave(
  welt: string | undefined,
  planId: number,
): UseMutationResult<
  unknown,
  Error,
  { targetId: number; packageTemplateId: number; count: number; arrivalTimestamp: number; arrivalEnd: number | null; critical: boolean }
> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: ({ targetId, ...input }) =>
      apiFetch(`/angriffsplaner/plans/${planId}/targets/${targetId}/waves`, {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteAttackPlanWave(
  welt: string | undefined,
  planId: number,
): UseMutationResult<void, Error, { targetId: number; waveId: number }> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: ({ targetId, waveId }) =>
      apiFetch<void>(`/angriffsplaner/plans/${planId}/targets/${targetId}/waves/${waveId}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useShiftAttackPlanArrivals(
  welt: string | undefined,
  planId: number,
): UseMutationResult<{ shifted: number }, Error, { shiftMinutes: number }> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ shifted: number }>(`/angriffsplaner/plans/${planId}/shift-arrivals`, {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useImportAttackPlanPool(
  welt: string | undefined,
  planId: number,
): UseMutationResult<{ imported: number; errors: string[] }, Error, { csvText: string }> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ imported: number; errors: string[] }>(`/angriffsplaner/plans/${planId}/pool/import`, {
        method: 'POST',
        body: { ...input, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useClearAttackPlanPool(welt: string | undefined, planId: number): UseMutationResult<void, Error, void> {
  const invalidate = useInvalidateAttackPlanDetail(welt, planId);
  return useMutation({
    mutationFn: () => apiFetch<void>(`/angriffsplaner/plans/${planId}/pool${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Gespeichertes Ergebnis der letzten Prüfung -- kein Seiteneffekt, kein Neu-Rechnen. */
export function useAttackPlanCheck(welt: string | undefined, planId: number): UseQueryResult<FeasibilityResult> {
  return useQuery({
    queryKey: queryKeys.attackPlanCheck(welt, planId),
    queryFn: ({ signal }) => apiFetch<FeasibilityResult>(`/angriffsplaner/plans/${planId}/check${weltQuery(welt)}`, { signal }),
    staleTime: 5_000,
  });
}

/** Rechnet neu UND speichert ("Verfügbarkeit prüfen"-Knopf) -- aktualisiert das gespeicherte Ergebnis sowie die Abdeckung in der Planübersicht. */
export function useRunAttackPlanCheck(welt: string | undefined, planId: number): UseMutationResult<FeasibilityResult, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<FeasibilityResult>(`/angriffsplaner/plans/${planId}/check`, {
        method: 'POST',
        body: welt ? { worldKey: welt } : {},
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.attackPlanCheck(welt, planId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.attackPlans(welt) }),
        // Der Plan selbst trägt `lastCheckedAt`/`lastCheckCoveragePercent` --
        // ohne diese Invalidierung bleibt die Detailseite (die diesen Wert
        // fürs "Zuletzt geprüft am …"-Banner nutzt) auf dem alten Stand.
        queryClient.invalidateQueries({ queryKey: queryKeys.attackPlan(welt, planId) }),
      ]);
    },
  });
}

export function useAttackPlanDsWorkbenchExport(
  welt: string | undefined,
  planId: number,
): UseMutationResult<DsWorkbenchExportResult, Error, void> {
  return useMutation({
    mutationFn: () => apiFetch<DsWorkbenchExportResult>(`/angriffsplaner/plans/${planId}/ds-workbench${weltQuery(welt)}`),
  });
}

/** "Workbenchbefehle nach Spieler gruppieren" -- unabhängig von einem eigenen Kriegsplan, ein eingefügter fremder Export wird nach Absender-Spieler aufgeteilt. */
export function useGroupPastedWorkbenchLines(
  welt: string | undefined,
): UseMutationResult<GroupPastedWorkbenchResult, Error, { text: string }> {
  return useMutation({
    mutationFn: ({ text }) =>
      apiFetch<GroupPastedWorkbenchResult>('/angriffsplaner/workbench-gruppieren', {
        method: 'POST',
        body: { text, ...(welt ? { worldKey: welt } : {}) },
      }),
  });
}

/* ------------------------------------------------------------------ */
/* AG-Planer (Stämme-Karte)                                            */
/* ------------------------------------------------------------------ */

/** Solange das Gelände noch geladen wird, alle 3 s nachfragen (Fortschritt + Fertig-Meldung). */
export function useAgPlanerMap(welt: string | undefined): UseQueryResult<AgPlanerMap> {
  return useQuery({
    queryKey: queryKeys.agPlanerMap(welt),
    queryFn: ({ signal }) => apiFetch<AgPlanerMap>(`/ag-planer/karte${weltQuery(welt)}`, { signal }),
    enabled: welt !== undefined,
    staleTime: 60_000,
    refetchInterval: (query) => {
      const status = query.state.data?.terrain.status;
      return status === 'loading' || status === 'missing' ? 3000 : false;
    },
  });
}

/** Rohe Kachelbytes (ein Byte je Kachel, zeilenweise) -- kein JSON, daher ein eigener fetch. */
export function useAgPlanerTerrain(welt: string | undefined, enabled: boolean): UseQueryResult<AgPlanerTerrain> {
  return useQuery({
    queryKey: queryKeys.agPlanerTerrain(welt),
    enabled: enabled && welt !== undefined,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const response = await fetch(`/api/ag-planer/gelaende${weltQuery(welt)}`, { credentials: 'same-origin', signal });
      if (!response.ok) throw new ApiError(response.status, 'Das Gelände konnte nicht geladen werden.');
      const width = Number(response.headers.get('X-Terrain-Width'));
      const height = Number(response.headers.get('X-Terrain-Height'));
      const tiles = new Uint8Array(await response.arrayBuffer());
      if (!width || !height || tiles.length !== width * height) throw new ApiError(500, 'Ungültige Geländedaten.');
      return { width, height, tiles };
    },
  });
}

export function useAgPlans(welt: string | undefined): UseQueryResult<{ plans: AgPlanSummary[] }> {
  return useQuery({
    queryKey: queryKeys.agPlans(welt),
    queryFn: ({ signal }) => apiFetch<{ plans: AgPlanSummary[] }>(`/ag-planer/plaene${weltQuery(welt)}`, { signal }),
    enabled: welt !== undefined,
    staleTime: 10_000,
  });
}

export function useAgPlan(welt: string | undefined, id: number | null): UseQueryResult<AgPlanDetail> {
  return useQuery({
    queryKey: queryKeys.agPlan(welt, id),
    queryFn: ({ signal }) => apiFetch<AgPlanDetail>(`/ag-planer/plaene/${id}${weltQuery(welt)}`, { signal }),
    enabled: welt !== undefined && id !== null,
    staleTime: 10_000,
  });
}

function useInvalidateAgPlans(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['ag-planer', 'plaene', welt] });
    await queryClient.invalidateQueries({ queryKey: ['ag-planer', 'plan', welt] });
    await queryClient.invalidateQueries({ queryKey: ['ag-planer', 'kandidaten', welt] });
  };
}

export function useCreateAgPlan(welt: string | undefined): UseMutationResult<{ plan: AgPlanSummary }, Error, { name: string }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ name }) =>
      apiFetch<{ plan: AgPlanSummary }>('/ag-planer/plaene', { method: 'POST', body: { name, ...(welt ? { worldKey: welt } : {}) } }),
    onSuccess: invalidate,
  });
}

export function useRenameAgPlan(welt: string | undefined): UseMutationResult<unknown, Error, { id: number; name: string }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id, name }) =>
      apiFetch(`/ag-planer/plaene/${id}`, { method: 'PUT', body: { name, ...(welt ? { worldKey: welt } : {}) } }),
    onSuccess: invalidate,
  });
}

export function useDeleteAgPlan(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/ag-planer/plaene/${id}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useImportAgCommands(
  welt: string | undefined,
): UseMutationResult<AgImportResult, Error, { id: number; text: string; mode: 'add' | 'replace' }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id, text, mode }) =>
      apiFetch<AgImportResult>(`/ag-planer/plaene/${id}/befehle`, {
        method: 'POST',
        body: { text, mode, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useClearAgCommands(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/ag-planer/plaene/${id}/befehle${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Die Farbe gilt welt-weit je Angriffstyp; die Ansicht zeigt sie sofort lokal, der Server speichert sie nur. */
export function useSetAgTypeColor(welt: string | undefined): UseMutationResult<void, Error, { typeKey: string; color: string }> {
  return useMutation({
    mutationFn: ({ typeKey, color }) =>
      apiFetch<void>('/ag-planer/farben', { method: 'PUT', body: { typeKey, color, ...(welt ? { worldKey: welt } : {}) } }),
  });
}

export function useImportAgNobles(
  welt: string | undefined,
): UseMutationResult<AgImportResult, Error, { id: number; text: string; mode: 'add' | 'replace' }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id, text, mode }) =>
      apiFetch<AgImportResult>(`/ag-planer/plaene/${id}/ags`, {
        method: 'POST',
        body: { text, mode, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}

export function useClearAgNobles(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/ag-planer/plaene/${id}/ags${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Kandidaten (Dörfer mit ungeplanten AGs) für einen gewählten Inc -- immer frisch, die Rechnung hängt an der aktuellen Uhrzeit. */
export function useNobleCandidates(
  welt: string | undefined,
  planId: number | null,
  commandId: number | null,
  gapMs: number,
): UseQueryResult<NobleCandidatesResult> {
  return useQuery({
    queryKey: ['ag-planer', 'kandidaten', welt, planId, commandId, gapMs] as const,
    queryFn: ({ signal }) =>
      apiFetch<NobleCandidatesResult>(
        `/ag-planer/plaene/${planId}/ag-kandidaten?befehl=${commandId}&abstand=${gapMs}${welt ? `&welt=${encodeURIComponent(welt)}` : ''}`,
        { signal },
      ),
    enabled: welt !== undefined && planId !== null && commandId !== null && Number.isInteger(gapMs) && gapMs >= 1,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function usePlanNoble(
  welt: string | undefined,
): UseMutationResult<void, Error, { planId: number; commandId: number; sourceX: number; sourceY: number; count: number; gapMs: number }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ planId, ...input }) =>
      apiFetch<void>(`/ag-planer/plaene/${planId}/ag-einplanen`, { method: 'POST', body: { ...input, ...(welt ? { worldKey: welt } : {}) } }),
    onSuccess: invalidate,
  });
}

export function useRemoveNobleAssignment(welt: string | undefined): UseMutationResult<void, Error, { planId: number; assignmentId: number }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ planId, assignmentId }) =>
      apiFetch<void>(`/ag-planer/plaene/${planId}/ag-einplanungen/${assignmentId}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Export der verplanten AGs im Workbench-Format; nur abrufen, solange der Dialog offen ist. */
export function useNobleExport(welt: string | undefined, planId: number | null, enabled: boolean): UseQueryResult<NobleExportResult> {
  return useQuery({
    queryKey: ['ag-planer', 'export', welt, planId] as const,
    queryFn: ({ signal }) => apiFetch<NobleExportResult>(`/ag-planer/plaene/${planId}/ag-export${weltQuery(welt)}`, { signal }),
    enabled: enabled && welt !== undefined && planId !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

/* ------------------------------------------------------------------ */
/* AG-Abfrage                                                          */
/* ------------------------------------------------------------------ */

export function useAgRequest(welt: string | undefined, enabled = true): UseQueryResult<AgRequestOverview> {
  return useQuery({
    queryKey: ['ag-abfrage', welt] as const,
    queryFn: ({ signal }) => apiFetch<AgRequestOverview>(`/ag-abfrage${weltQuery(welt)}`, { signal }),
    enabled: enabled && welt !== undefined,
    staleTime: 10_000,
  });
}

function useInvalidateAgRequest(welt: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['ag-abfrage', welt] });
  };
}

export function useAddAgEntry(welt: string | undefined): UseMutationResult<void, Error, { x: number; y: number; count: number }> {
  const invalidate = useInvalidateAgRequest(welt);
  return useMutation({
    mutationFn: (input) =>
      apiFetch<void>('/ag-abfrage/eintraege', { method: 'POST', body: { ...input, ...(welt ? { worldKey: welt } : {}) } }),
    onSuccess: invalidate,
  });
}

export function useAddAgEntries(welt: string | undefined): UseMutationResult<AgBulkResult, Error, { text: string }> {
  const invalidate = useInvalidateAgRequest(welt);
  return useMutation({
    mutationFn: ({ text }) =>
      apiFetch<AgBulkResult>('/ag-abfrage/eintraege/bulk', { method: 'POST', body: { text, ...(welt ? { worldKey: welt } : {}) } }),
    onSuccess: invalidate,
  });
}

export function useUpdateAgEntry(welt: string | undefined): UseMutationResult<void, Error, { id: number; count: number }> {
  const invalidate = useInvalidateAgRequest(welt);
  return useMutation({
    mutationFn: ({ id, count }) =>
      apiFetch<void>(`/ag-abfrage/eintraege/${id}`, { method: 'PUT', body: { count, ...(welt ? { worldKey: welt } : {}) } }),
    onSuccess: invalidate,
  });
}

export function useDeleteAgEntry(welt: string | undefined): UseMutationResult<void, Error, { id: number }> {
  const invalidate = useInvalidateAgRequest(welt);
  return useMutation({
    mutationFn: ({ id }) => apiFetch<void>(`/ag-abfrage/eintraege/${id}${weltQuery(welt)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useCreateAgRequest(welt: string | undefined): UseMutationResult<unknown, Error, void> {
  const invalidate = useInvalidateAgRequest(welt);
  return useMutation({
    mutationFn: () => apiFetch('/ag-abfrage/anfrage', { method: 'POST', body: welt ? { worldKey: welt } : {} }),
    onSuccess: invalidate,
  });
}

/** Übernimmt den Bestand der AG-Abfrage in einen AG-Planer-Plan. */
export function useImportAgNoblesFromRequest(
  welt: string | undefined,
): UseMutationResult<AgImportResult, Error, { id: number; mode: 'add' | 'replace' }> {
  const invalidate = useInvalidateAgPlans(welt);
  return useMutation({
    mutationFn: ({ id, mode }) =>
      apiFetch<AgImportResult>(`/ag-planer/plaene/${id}/ags-aus-abfrage`, {
        method: 'POST',
        body: { mode, ...(welt ? { worldKey: welt } : {}) },
      }),
    onSuccess: invalidate,
  });
}
